import {
  ApplicantStatus,
  ExecutionModes,
  RetryIdentityVerificationStatus,
  Verification as VerificationA,
  VerificationNames,
  VerificationStatus
} from "shared/types";
import {
  Verification as VerificationB,
  VerificationSummary
} from "views/applications-id/components/ApplicantsTable/types";
import { Applicant, Application } from "types/application";

type GeneralVerification = Partial<VerificationA | VerificationB>;

export type ReportState =
  // Blocked
  | "idv_override_required"
  | "eviction_blocked_by_credit"
  | "idv_retrying"
  // Execution mode states
  | "manual_pending"
  | "conditional_pending"
  | "conditional_skipped"
  // In progress
  | "rerun_in_progress"
  | "submitted_in_progress"
  | "processing"
  | "started"
  | "pending"
  // Terminal
  | "canceled"
  | "no_data_available"
  | "finished"
  | "failed"
  | "finished_with_warnings"
  | "skipped"
  // Defer to slice (for ReportsCard)
  | "render_report"
  | "render_credit_report"
  | "render_combined_income_report"
  | "render_pet_screening"
  | "render_warnings_report";

interface VerificationStateHandlerOptions {
  verification?: GeneralVerification;
  applicant?: Applicant;
  application?: Application;
}

export class VerificationStateHandler {
  private verification?: GeneralVerification;
  private applicant?: Applicant;
  private application?: Application;

  constructor(opts: VerificationStateHandlerOptions) {
    this.verification = opts.verification;
    this.applicant = opts.applicant;
    this.application = opts.application;
  }

  private isIdentityVerificationRetrying = (): boolean => {
    if (this.verification?.name !== VerificationNames.IdentityVerification)
      return false;

    const isPendingOrStarted =
      this.verification?.status === VerificationStatus.Pending ||
      this.verification?.status === VerificationStatus.Started;

    const isRetryActive = [
      RetryIdentityVerificationStatus.Pending,
      RetryIdentityVerificationStatus.InProgress
    ].includes(
      this.applicant?.retry_identity_verification_status ||
        RetryIdentityVerificationStatus.NotAvailable
    );

    return isPendingOrStarted && isRetryActive;
  };

  private isEvictionBlockedByCredit = (): boolean => {
    if (this.verification?.name !== VerificationNames.EvictionReport)
      return false;
    if (this.verification.status !== VerificationStatus.Pending) return false;
    if (this.applicant?.application_status !== ApplicantStatus.Submitted)
      return false;
    if (this.verification.settings?.execution_mode === ExecutionModes.Manual)
      return false;

    const creditReport = this.applicant.verifications?.find(
      (v) => v.name === VerificationNames.CreditReport
    );

    return (
      !!creditReport &&
      [
        VerificationStatus.Failed,
        VerificationStatus.FinishedWithWarnings
      ].includes(creditReport.status)
    );
  };

  private isBlockedByIdentityOverride = (): boolean => {
    if (!this.verification) return false;

    const isIdvOverrideRequired =
      this.application?.requires_overrides_identity_verification?.applicants.some(
        ({ id }) => id === this.applicant?.id
      );
    if (!isIdvOverrideRequired || !this.verification.name) return false;

    return [
      VerificationNames.CreditReport,
      VerificationNames.CriminalReport,
      VerificationNames.EvictionReport
    ].includes(this.verification.name);
  };

  private isReportProcessing = (): boolean => {
    // PetScreening handles this independently
    if (this.verification?.name === VerificationNames.PetScreeningReport)
      return false;

    const isCurrentlyProcessing =
      this.verification?.status === VerificationStatus.Processing;

    const isVerifiedIncomeProcessing =
      this.verification?.name === VerificationNames.IncomeVerification &&
      this.applicant?.verifications.find(
        (v) => v.name === VerificationNames.VerifiedIncomeVerification
      )?.status === VerificationStatus.Processing;

    return isCurrentlyProcessing || isVerifiedIncomeProcessing;
  };

  public getCreditReportStatus = () => {
    const activeCreditVerification =
      this.applicant?.credit_report_verifications?.find(
        (cr) => !cr.archived_at
      );
    return activeCreditVerification?.status ?? VerificationStatus.Pending;
  };

  /*
   * isRender - Whether the function is being called for an actual state
   *            (ReportStatusBadge) or for rendering logic (ReportsCard)
   */
  public getReportState = (opts?: {
    isRender: boolean;
    // eslint-disable-next-line sonarjs/cognitive-complexity
  }): ReportState | VerificationStatus => {
    const { isRender } = opts || {};

    if (this.verification?.name === VerificationNames.CreditReport)
      return isRender ? "render_credit_report" : this.getCreditReportStatus();

    if (
      this.verification?.name === VerificationNames.CombinedIncome &&
      isRender
    )
      return "render_combined_income_report";

    if (this.isIdentityVerificationRetrying()) return "idv_retrying";

    if (
      this.verification?.status === VerificationStatus.Pending &&
      this.verification.settings?.execution_mode === ExecutionModes.Manual
    )
      return "manual_pending";

    const isPendingOrSkipped =
      this.verification?.status === VerificationStatus.Pending ||
      this.verification?.status === VerificationStatus.Skipped;
    if (
      this.verification?.settings?.execution_mode ===
        ExecutionModes.Conditionally &&
      isPendingOrSkipped
    ) {
      const conditionalStatus = this.verification.status as
        | VerificationStatus.Pending
        | VerificationStatus.Skipped;
      return `conditional_${conditionalStatus}`;
    }

    if (this.isEvictionBlockedByCredit()) return "eviction_blocked_by_credit";

    if (
      this.verification?.status === VerificationStatus.Pending &&
      this.verification.name !== VerificationNames.PetScreeningReport
    ) {
      if (this.verification.is_rerun) return "rerun_in_progress";
      if (this.applicant?.application_status === ApplicantStatus.Submitted)
        return "submitted_in_progress";
    }

    if (this.isBlockedByIdentityOverride()) return "idv_override_required";

    if (this.isReportProcessing()) return "processing";

    if (
      this.verification?.name === VerificationNames.PetScreeningReport &&
      isRender
    )
      return "render_pet_screening";

    // Warnings report is a derived/computed report: skip the per-verification
    // status fallbacks (Pending/Canceled/Started/NoDataAvailable) because it has
    // no upstream vendor and is always available once an applicant exists.
    if (
      this.verification?.name === VerificationNames.WarningsReport &&
      isRender
    )
      return "render_warnings_report";

    if (
      this.verification?.status &&
      [
        VerificationStatus.Pending,
        VerificationStatus.Canceled,
        VerificationStatus.Started,
        VerificationStatus.NoDataAvailable,
        VerificationStatus.Failed,
        VerificationStatus.FinishedWithWarnings,
        VerificationStatus.Skipped
      ].includes(this.verification.status)
    )
      return this.verification.status;

    if (isRender) return "render_report";

    return "finished";
  };

  /*
   * ReportStatusBadge specific functions -- not relevant in ReportsCard
   */
  public isCriminalApprovalRequired = (): boolean => {
    if (this.verification?.name !== VerificationNames.CriminalReport)
      return false;

    const summary = this.verification.summary as
      | VerificationSummary
      | undefined;
    return Boolean(summary?.require_approval);
  };
}
